import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Ship, Fuel, Globe, Target, BarChart, Zap, Thermometer, Database, FileText, Anchor, Snowflake, Wind, Droplets, Award } from "lucide-react";

const CIICalculations = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      collection: "VesselModel",
      fields: [
        "name", "imoNumber", "vesselType", "deadweight", "dimensions", 
        "iceClassShip", "iceClassType", "fuelTypes", "electricalEngineType", 
        "sfocUsed", "totalCargoCapacity", "dwtEnhanced"
      ],
      purpose: "Vessel characteristics and configuration"
    },
    {
      collection: "VesselRawData",
      fields: [
        "Distance Sailed (NM)", "Distance Sailed in Laden Condition (NM)", 
        "Main_Engine_Cons_MT_*", "Aux_Engine_Cons_MT_*", "Boiler_Cons_MT_*",
        "Incinerator_Cons_MT_*", "IGG_Cons_MT_*", "Coolingkwh", "Dischargekwh",
        "Boiler_Cargo_Heating", "Boiler_Cargo_Discharge", "ReeferkWh",
        "Shore_Power_Energy", "Port_Days", "Port_Reefers_Arrival", "Port_Reefers_Departure",
        "Report_Type", "Sailing in ice condition", "STS Operation"
      ],
      purpose: "Operational data for annual aggregation"
    },
    {
      collection: "FuelTableModel",
      fields: ["Fuel_Type", "CF_C02", "CO2_EF", "CF_CO2", "emissionFactor"],
      purpose: "CO₂ emission factors by fuel type"
    }
  ];

  const coreCalculations = [
    {
      id: "co2-emissions",
      title: "CO₂ Emissions Calculation",
      description: "Calculates total CO₂ emissions from all fuel types consumed",
      formula: "CO₂ Emitted = Σ(Fuel Quantity × CO₂ Emission Factor)",
      subFormulas: [
        "For each fuel type: HFO, LFO, MGO, etc.",
        "Quantity from: MainEngine + AuxEngine + Boiler + Incinerator + IGG",
        "Emission factors from FuelTableModel or default values",
        "Total across all fuel types consumed"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "effective-distance",
      title: "Effective Distance Calculation",
      description: "Calculates distance used in CII denominator after adjustments",
      formula: "Distance For CII = Dt (Total) - Dx (Emergency + Ice Condition)",
      subFormulas: [
        "Dt = Total annual distance sailed from all records",
        "Dx = Emergency + Ice condition distances",
        "Emergency: Report_Type = 'Emergency Report'",
        "Ice Condition: Sailing in ice condition = 'Yes'",
        "Must be positive value, minimum 0.1 NM"
      ],
      icon: <Globe className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "fuel-deductions",
      title: "Fuel Consumption Deductions",
      description: "Adjusts fuel consumption for special conditions and operations",
      formula: "Adjusted Fuel = Quantity - (FCvoyagej + TFj + ElectricalDeduction)",
      subFormulas: [
        "FCvoyagej: Emergency/Ice condition fuel consumption",
        "TFj: Tanker factor deductions (for tanker vessels only)",
        "ElectricalDeduction = (0.75 - 0.03 × y1) × (Fcelectrical + FCboiler + FCOthers)",
        "y1 = Calculation Year - 2023"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "tanker-factors",
      title: "Tanker Specific Factors",
      description: "Applies tanker-specific correction factors for STS and shuttle operations",
      formula: "TFj = (1 - AF) × Fuel Quantity",
      subFormulas: [
        "AFTankerSTS = 6.1742 × Capacity⁻⁰·²⁴⁶ (STS operations)",
        "AFTankerShuttle = 5.6805 × Capacity⁻⁰·²⁰⁸ (Shuttle tankers)",
        "Applied only for Tanker vessel types",
        "Conditions: STS='ON' or shuttleTanker='YES'"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    }
  ];

  const vesselFactors = [
    {
      id: "ice-class-factor",
      title: "Ice Class Factor (fic)",
      description: "Adjustment for ice-class strengthened vessels",
      formula: "fic = f(iceClassType, capacity)",
      values: [
        "IA: 1.0099 + 95.1 ÷ capacity",
        "IA Super: 1.0151 + 228.7 ÷ capacity",
        "IB: 1.0067 + 62.7 ÷ capacity",
        "IC: 1.0041 + 58.5 ÷ capacity",
        "Other: 1.0"
      ],
      icon: <Snowflake className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "block-coefficient",
      title: "Block Coefficient Factor (ficb)",
      description: "Correction for actual vs design block coefficient",
      formula: "ficb = CBdesign ÷ CB (if CBdesign/CB > 1)",
      values: [
        "CB = Displacement ÷ (Lpp × B × T)",
        "CBdesign from vessel type and capacity ranges",
        "Applied to: Bulk carriers, Tankers, General cargo",
        "Otherwise: ficb = 1"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: "chemical-tanker",
      title: "Chemical Tanker Factor (fc)",
      description: "Adjustment for chemical tankers based on capacity ratio",
      formula: "fc = (capacity ÷ totalCargoCapacity)⁻⁰·⁷ - 0.014",
      values: [
        "Applied when: vesselType = 'Chemical Tanker'",
        "Condition: capacity_ratio < 0.98",
        "Otherwise: fc = 1",
        "Ratio = capacity ÷ totalCargoCapacity"
      ],
      icon: <Droplets className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "dwt-enhanced",
      title: "DWT Enhanced Factor (fivse)",
      description: "Adjustment for vessels with enhanced deadweight",
      formula: "fivse = capacity ÷ DWT_Enhanced",
      values: [
        "Applied when: dwtEnhanced > 0",
        "DWT_Enhanced from vessel record",
        "Default: fivse = 1 if no enhanced DWT",
        "Only affects certain vessel types"
      ],
      icon: <BarChart className="w-5 h-5" />,
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    }
  ];

  const ciiRatingCalculation = [
    {
      id: "required-cii",
      title: "Required CII Calculation",
      description: "Calculates the required CII value based on vessel type and year",
      formula: "Required CII = [(100 - Z) ÷ 100] × a × Capacity⁻ᶜ",
      subFormulas: [
        "a, c: Vessel-type specific parameters",
        "Z: Year reduction factor (2023=5, 2024=7, 2025=9, etc.)",
        "Capacity: vessel.deadweight in tonnes",
        "Reference G2: a × Capacity⁻ᶜ",
        "Reference G3: Reduced by Z%"
      ],
      icon: <Target className="w-5 h-5" />,
      borderColor: "border-emerald-200",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-700",
    },
    {
      id: "attained-cii",
      title: "Attained CII Calculation",
      description: "Calculates the actual CII achieved by the vessel",
      formula: "Attained CII = ΣNum ÷ (fi × fm × fc × fivse × Capacity × Distance) × 1,000,000",
      subFormulas: [
        "Num = Cj × Adjusted Fuel Consumption",
        "Denominator: All correction factors multiplied",
        "Capacity: Deadweight in tonnes",
        "Distance: Effective distance in nautical miles",
        "Result: g CO₂/tonne-nautical mile"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    },
    {
      id: "rating-determination",
      title: "Rating Determination",
      description: "Determines the CII rating (A-E) based on attained/required ratio",
      formula: "Ratio = Attained CII ÷ Required CII",
      subFormulas: [
        "A: Ratio ≤ d1 (typically 0.80)",
        "B: d1 < Ratio ≤ d2 (typically 0.90)",
        "C: d2 < Ratio ≤ d3 (typically 1.00)",
        "D: d3 < Ratio ≤ d4 (typically 1.10)",
        "E: Ratio > d4"
      ],
      icon: <Award className="w-5 h-5" />,
      borderColor: "border-violet-200",
      bgColor: "bg-violet-50",
      textColor: "text-violet-700",
    }
  ];

  const emissionFactors = [
    { fuel: "HFO", factor: 3.114, source: "IMO Default" },
    { fuel: "LFO", factor: 3.151, source: "IMO Default" },
    { fuel: "MGO", factor: 3.206, source: "IMO Default" },
    { fuel: "MDO", factor: 3.206, source: "IMO Default" },
    { fuel: "LNG", factor: 2.750, source: "IMO Default" },
    { fuel: "Methanol", factor: 1.375, source: "IMO Default" },
    { fuel: "Ethane", factor: 1.47, source: "IMO Default" },
    { fuel: "Biofuel", factor: 0.0, source: "Carbon Neutral" },
    { fuel: "HSFO", factor: 3.114, source: "IMO Default" },
    { fuel: "LSFO", factor: 3.114, source: "IMO Default" },
  ];

  const calculationProcess = [
    { step: 1, title: "Data Collection", description: "Fetch vessel and operational data from MongoDB collections" },
    { step: 2, title: "Annual Aggregation", description: "Aggregate all operational data for the calculation year" },
    { step: 3, title: "Fuel Consumption Analysis", description: "Calculate total fuel consumption by type and purpose" },
    { step: 4, title: "Distance Calculation", description: "Calculate effective distance after adjustments" },
    { step: 5, title: "Vessel Factors", description: "Apply all vessel-specific correction factors" },
    { step: 6, title: "CII Calculation", description: "Calculate attained and required CII values" },
    { step: 7, title: "Rating Determination", description: "Determine CII rating (A-E) based on ratio" },
    { step: 8, title: "Results Compilation", description: "Compile all results for API response" },
  ];

  const voyageTypeClassification = [
    { type: "NORMAL", condition: "Standard operations", impact: "No distance/fuel deduction" },
    { type: "EMERGENCY", condition: "Report_Type = 'Emergency Report'", impact: "Distance and fuel deducted from CII" },
    { type: "ICE_CONDITION", condition: "Sailing in ice condition = 'Yes'", impact: "Distance and fuel deducted from CII" },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#faeebe] rounded-lg">
              <Calculator className="w-6 h-6 text-[#b7950b]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                CII Calculations Methodology
              </h2>
              <p className="text-gray-600 text-sm">
                Carbon Intensity Indicator calculations
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200 space-y-8">
            {/* Data Sources Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Data Sources & Collections</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-800 mb-2">{source.collection}</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{source.purpose}</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="text-xs text-gray-500 mb-2">Fields:</div>
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">• {field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Voyage Type Classification */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Voyage Type Classification</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {voyageTypeClassification.map((voyage, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${
                      voyage.type === 'EMERGENCY' ? 'border-red-200 bg-red-50' :
                      voyage.type === 'ICE_CONDITION' ? 'border-blue-200 bg-blue-50' :
                      'border-green-200 bg-green-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-bold ${
                          voyage.type === 'EMERGENCY' ? 'text-red-700' :
                          voyage.type === 'ICE_CONDITION' ? 'text-blue-700' :
                          'text-green-700'
                        }`}>
                          {voyage.type}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          voyage.type === 'EMERGENCY' ? 'bg-red-100 text-red-600' :
                          voyage.type === 'ICE_CONDITION' ? 'bg-blue-100 text-blue-600' :
                          'bg-green-100 text-green-600'
                        }`}>
                          {voyage.impact.split(' ')[0]}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-1">{voyage.condition}</p>
                      <p className="text-xs text-gray-600">{voyage.impact}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Core Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Vessel Correction Factors */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Vessel Correction Factors</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {vesselFactors.map((factor) => (
                  <div
                    key={factor.id}
                    className={`p-5 rounded-lg border ${factor.borderColor} ${factor.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${factor.textColor} bg-white`}>
                        {factor.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${factor.textColor}`}>
                          {factor.title}
                        </h4>
                        <p className="text-sm text-gray-600">{factor.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {factor.formula}
                        </div>
                      </div>
                      
                      {factor.values && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Values:</p>
                          <div className="space-y-2">
                            {factor.values.map((value, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100">
                                {value}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CII Rating Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">CII Rating Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {ciiRatingCalculation.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Parameters:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>


            {/* Calculation Process Flow */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Process Flow</h3>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {calculationProcess.map((step) => (
                    <div key={step.step} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                          {step.step}
                        </div>
                        <div className="font-semibold text-gray-800">{step.title}</div>
                      </div>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CIICalculations;